import {
  BRUTEFORCE_CONDITION_TYPES,
  BRUTEFORCE_DIRECTION_IDS,
  DEFAULT_BRUTEFORCE_TARGET,
  getBruteforceMetric,
  getBruteforceMetricValue,
} from "./defaults.js";
import { BruteforceEvaluator } from "./evaluator.js";
import { mutateScriptEvents } from "./mutator.js";
import {
  formatScriptFrame,
  parseScriptFrameToken,
  parseScriptText,
  serializeScriptEvents,
} from "./script-format.js";

const RUNNER_STATE = {
  IDLE: "idle",
  RUNNING: "running",
  STOPPED: "stopped",
  ERROR: "error",
};

function parseFiniteNumber(value, label) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    throw new Error(`${label} must be a number.`);
  }
  return parsed;
}

function scoreCandidate(result, target) {
  const value = getBruteforceMetricValue(result.telemetry, target.metric);
  switch (target.direction) {
    case BRUTEFORCE_DIRECTION_IDS.INCREASE:
      return value;
    case BRUTEFORCE_DIRECTION_IDS.DECREASE:
      return -value;
    case BRUTEFORCE_DIRECTION_IDS.CONVERGE:
      return -Math.abs(value - target.value);
    default:
      return value;
  }
}

export class BruteforceRunner {
  constructor({ onUpdate = null } = {}) {
    this.onUpdate = onUpdate;
    this.evaluator = new BruteforceEvaluator();
    this.runToken = 0;
    this.state = RUNNER_STATE.IDLE;
    this.bestResult = null;
    this.bestScriptText = "";
    this.bestScriptEvents = [];
    this.iterations = 0;
  }

  getStatus() {
    return {
      state: this.state,
      iterations: this.iterations,
      bestResult: this.bestResult,
      bestScriptText: this.bestScriptText,
    };
  }

  getBestScriptText() {
    return this.bestScriptText;
  }

  stop() {
    if (this.state !== RUNNER_STATE.RUNNING) {
      return;
    }
    this.runToken += 1;
    this.state = RUNNER_STATE.STOPPED;
    this.emit({ message: "Stopped." });
  }

  async start({ levelId, baseScriptText, spikesKill = false, settings }) {
    try {
      this.stop();
      this.bestResult = null;
      this.bestScriptText = baseScriptText;
      this.bestScriptEvents = parseScriptText(baseScriptText);
      this.iterations = 0;
      const target = this.parseSettings(settings);
      const resolvedLevelId = target.levelId ?? levelId;
      const runToken = this.runToken + 1;
      this.runToken = runToken;
      this.state = RUNNER_STATE.RUNNING;

      const baseResult = await this.evaluator.evaluate({
        levelId: resolvedLevelId,
        scriptText: baseScriptText,
        scriptEvents: this.bestScriptEvents,
        targetFrame: target.targetFrame,
        spikesKill,
        metric: target.metric,
      });
      if (this.runToken !== runToken) {
        return;
      }

      const rankedBaseResult = {
        ...baseResult,
        targetValue: getBruteforceMetricValue(baseResult.telemetry, target.metric),
        rankingScore: scoreCandidate(baseResult, target),
        label: "Base run",
      };
      this.bestResult = this.satisfiesTarget(rankedBaseResult, target) ? rankedBaseResult : null;
      this.bestScriptText = baseScriptText;
      this.emit({
        levelId: resolvedLevelId,
        settings,
        targetFrame: target.targetFrame,
        message: this.bestResult ? "Running..." : "Running... no valid run yet.",
      });

      const loop = async () => {
        try {
          if (this.runToken !== runToken || this.state !== RUNNER_STATE.RUNNING) {
            return;
          }
          const batchStartedAt = performance.now();
          let attempts = 0;
          while (performance.now() - batchStartedAt < 24 && attempts < 6) {
            const candidateEvents = mutateScriptEvents(this.bestScriptEvents, target.windowConfig);
            const candidateScriptText = serializeScriptEvents(candidateEvents);
            const candidateResult = await this.evaluator.evaluate({
              levelId: resolvedLevelId,
              scriptText: candidateScriptText,
              scriptEvents: candidateEvents,
              targetFrame: target.targetFrame,
              spikesKill,
              metric: target.metric,
            });
            if (this.runToken !== runToken || this.state !== RUNNER_STATE.RUNNING) {
              return;
            }
            this.iterations += 1;
            attempts += 1;
            if (this.isCandidateBetter(candidateResult, this.bestResult, target)) {
              this.bestResult = {
                ...candidateResult,
                targetValue: getBruteforceMetricValue(candidateResult.telemetry, target.metric),
                rankingScore: scoreCandidate(candidateResult, target),
                label: `Iteration ${this.iterations}`,
              };
              this.bestScriptText = candidateScriptText;
              this.bestScriptEvents = candidateEvents;
            }
          }
          this.emit({
            levelId: resolvedLevelId,
            settings,
            targetFrame: target.targetFrame,
            message: this.bestResult ? "Running..." : "Running... no valid run yet.",
          });
          if (this.runToken === runToken && this.state === RUNNER_STATE.RUNNING) {
            globalThis.setTimeout(() => {
              void loop();
            }, 0);
          }
        } catch (error) {
          this.fail(error);
        }
      };

      globalThis.setTimeout(() => {
        void loop();
      }, 0);
    } catch (error) {
      this.fail(error);
    }
  }

  parseSettings(settings) {
    const levelId = Math.max(1, Number(settings.levelId ?? 1) || 1);
    const targetConfig = settings?.target && typeof settings.target === "object"
      ? settings.target
      : DEFAULT_BRUTEFORCE_TARGET;
    const targetMetric = getBruteforceMetric(targetConfig.metric).id;
    const targetDirection = Object.values(BRUTEFORCE_DIRECTION_IDS).includes(targetConfig.direction)
      ? targetConfig.direction
      : DEFAULT_BRUTEFORCE_TARGET.direction;
    const targetValue = targetDirection === BRUTEFORCE_DIRECTION_IDS.CONVERGE
      ? parseFiniteNumber(targetConfig.value ?? 0, `${getBruteforceMetric(targetMetric).label} converge target`)
      : null;
    const targetFrame = parseScriptFrameToken(settings.targetTime);

    const conditions = (Array.isArray(settings.conditions) ? settings.conditions : []).map((condition, index) => {
      const metric = getBruteforceMetric(condition.metric).id;
      const type = Object.values(BRUTEFORCE_CONDITION_TYPES).includes(condition.type)
        ? condition.type
        : BRUTEFORCE_CONDITION_TYPES.AT_LEAST;
      const value = parseFiniteNumber(condition.value ?? 0, `Condition ${index + 1} value`);
      return { metric, type, value };
    });

    return {
      levelId,
      targetFrame,
      metric: targetMetric,
      direction: targetDirection,
      value: targetValue,
      metricLabel: getBruteforceMetric(targetMetric).label,
      directionLabel: targetDirection,
      conditions,
      targetLabel: formatScriptFrame(targetFrame),
      windowConfig: {
        maxFrameHint: targetFrame,
        modify: settings.mutations?.modify,
        add: settings.mutations?.add,
        delete: settings.mutations?.delete,
      },
    };
  }

  satisfiesTarget(candidateResult, target) {
    if (!candidateResult.reachedTarget) {
      return false;
    }
    for (const condition of target.conditions) {
      const value = getBruteforceMetricValue(candidateResult.telemetry, condition.metric);
      if (condition.type === BRUTEFORCE_CONDITION_TYPES.AT_LEAST && value < condition.value) {
        return false;
      }
      if (condition.type === BRUTEFORCE_CONDITION_TYPES.AT_MOST && value > condition.value) {
        return false;
      }
    }
    return true;
  }

  isCandidateBetter(candidateResult, currentBest, target) {
    const candidateValid = this.satisfiesTarget(candidateResult, target);
    const currentValid = currentBest ? this.satisfiesTarget(currentBest, target) : false;
    if (candidateValid && !currentValid) {
      return true;
    }
    if (!candidateValid) {
      return false;
    }
    if (!currentValid) {
      return true;
    }
    const candidateScore = scoreCandidate(candidateResult, target);
    const currentScore = currentBest?.rankingScore ?? scoreCandidate(currentBest, target);
    return candidateScore > currentScore;
  }

  emit(extra = {}) {
    this.onUpdate?.({
      ...this.getStatus(),
      ...extra,
    });
  }

  fail(error) {
    this.runToken += 1;
    this.state = RUNNER_STATE.ERROR;
    this.emit({ message: error instanceof Error ? error.message : String(error) });
  }
}
